import ErrorBoundary from "@/components/ErrorBoundary";
import { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import "./App.css";
// Non-lazy: wrappers and providers (assumed to be small)
import { PrivateRoute, PublicRoute } from "./components/custom/AuthGuard";
import Loader from "./components/custom/Loader";
import { AudioProvider } from "./context/AudioContext";
import { SessionProvider } from "./context/SessionContext";
import { UserProvider } from "./context/UserContext";

// Lazy load all route components
const DashboardLayout = lazy(() => import("./layouts/DashboardLayout"));
const UserHome = lazy(() => import("./modules/home/components/UserHome"));
const SessionDashboard = lazy(() =>
  import("./modules/home/components/SessionDashboard")
);
const Middle = lazy(() => import("./modules/home/components/Middle"));
const RichTextEditor = lazy(() =>
  import("./modules/home/components/Editor/RichTextEditor")
);
const ViewUsage = lazy(() =>
  import("./modules/home/components/Usage/ViewUsage")
);
const TranscriptionInterface = lazy(() =>
  import("./modules/home/components/Audio/TranscriptionInterface")
);
const SettingsLayout = lazy(() => import("./layouts/SettingsLayout"));
const ProfilePage = lazy(() =>
  import("./modules/settings/Profile/ProfilePage")
);
const UserViewUsage = lazy(() =>
  import("./modules/settings/Usage/UserViewUsage")
);
const ViewSubscriptionPage = lazy(() =>
  import("./modules/settings/Subscription/ViewSubscriptionPage")
);

const Login = lazy(() => import("./page/auth/Login"));
const Register = lazy(() => import("./page/auth/Register"));
// const ErrorPage = lazy(() => import("./page/ErrorPage"));

function App() {
  return (
    <BrowserRouter>
      <UserProvider>
        <SessionProvider>
          <AudioProvider>
            <Suspense fallback={<Loader />}>
              <Routes>
                {/* Public Routes (Auth) */}
                <Route element={<PublicRoute />}>
                  <Route
                    path="/auth/login"
                    element={
                      <ErrorBoundary>
                        <Login />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="/auth/register"
                    element={
                      <ErrorBoundary>
                        <Register />
                      </ErrorBoundary>
                    }
                  />
                </Route>

                {/* Private Routes (Authenticated) */}
                <Route element={<PrivateRoute />}>
                  {/* Dashboard and related routes */}
                  <Route
                    path="/"
                    element={
                      <ErrorBoundary>
                        <DashboardLayout />
                      </ErrorBoundary>
                    }
                  >
                    <Route index element={<UserHome />} />
                    <Route path="session/:sessionId" element={<SessionDashboard />}>
                      <Route index element={<Middle />} />
                    </Route>
                    <Route path="session/:sessionId/edit" element={<RichTextEditor />} />
                    <Route path="session/:sessionId/usage" element={<ViewUsage />} />
                    <Route path="audio/record/:sessionId" element={<TranscriptionInterface />} />
                  </Route>

                  {/* Settings and related routes */}
                  <Route
                    path="/settings"
                    element={
                      <ErrorBoundary>
                        <SettingsLayout />
                      </ErrorBoundary>
                    }
                  >
                    <Route index element={<ProfilePage />} />
                    <Route path="profile" element={<ProfilePage />} />
                    <Route path="usage" element={<UserViewUsage />} />
                    <Route path="subscription" element={<ViewSubscriptionPage />} />
                  </Route>
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </AudioProvider>
        </SessionProvider>
      </UserProvider>
    </BrowserRouter>
  );
}

export default App;
